/**
 * Rapprochement de deux cartes (deux établissements, même date et même canal).
 *
 * Pourquoi ce fichier existe : section, catégorie ET produit établissement sont
 * tous rattachés à un site (contrainte d'unicité (name, establishment) côté
 * lemoulinapi). Leurs identifiants ne veulent donc RIEN dire d'un site à
 * l'autre. Rapprocher sur `id` afficherait des différences imaginaires partout.
 *
 * Les clés retenues sont celles qui sont réellement partagées :
 *  - sections et catégories : leur NOM (unique par établissement) ;
 *  - produits : le produit GÉNÉRIQUE (`genericId`), commun aux deux sites ;
 *  - suggestions : elles appartiennent au produit établissement et n'ont pas
 *    d'équivalent générique — on les rapproche sur le générique du parent + leur
 *    nom.
 *
 * Repli sur le nom quand le générique manque : mieux vaut un rapprochement par
 * nom qu'une fausse différence.
 */

/** Identité d'une ligne, indépendante du site : générique, ou nom en repli. */
function rowIdentity(row) {
  if (row.kind === "suggestion") {
    return `suggestion:${row.genericId || "?"}:${(row.name || "").trim().toLowerCase()}`;
  }
  return row.genericId
    ? `product:${row.genericId}`
    : `product-name:${(row.name || "").trim().toLowerCase()}`;
}

/**
 * Pose sur chaque ligne la clé qui servira à la rapprocher de l'autre site.
 *
 * À appeler sur la réponse BRUTE de l'API, avant tout filtre ou tri : le rang
 * d'occurrence ci-dessous n'a de sens que dans l'ordre d'origine.
 *
 * Le rang existe pour un cas réel : un produit rattaché à DEUX sous-catégories
 * d'une même catégorie apparaît légitimement deux fois dans la carte (le tunnel
 * B2C empile un bloc par sous-catégorie) — « Part de galette des rois », dans
 * Desserts. Sans lui, les deux occurrences se rapprochaient l'une l'autre et une
 * ligne disparaissait de la comparaison.
 *
 * La sous-catégorie, elle, ne peut PAS entrer dans la clé : elle est propre à
 * l'établissement. Le Gaspacho est classé « Soupes » à Lyon 8 et « Entrées » à
 * Lyon 9 — même générique, même catégorie, deux sous-catégories : l'inclure
 * faisait passer un produit présent des deux côtés pour absent de l'un.
 *
 * Limite assumée : quand un produit apparaît plusieurs fois des deux côtés, les
 * occurrences s'apparient dans l'ordre du back-office, pas par sous-catégorie.
 */
export function annotateMergeKeys(offer) {
  (offer?.sections || []).forEach((section) => {
    section.categories.forEach((category) => {
      const seen = new Map();
      category.products.forEach((row) => {
        const identity = rowIdentity(row);
        const rank = seen.get(identity) || 0;
        seen.set(identity, rank + 1);
        // Première occurrence sans suffixe : les clés restent lisibles, et une
        // catégorie sans doublon produit exactement l'identité.
        row.mergeKey = rank ? `${identity}#${rank}` : identity;
      });
    });
  });

  return offer;
}

/**
 * Clé de rapprochement d'une ligne, posée par annotateMergeKeys.
 * Repli sur l'identité seule si l'annotation n'a pas eu lieu — mieux vaut un
 * rapprochement approximatif qu'une clé `undefined` partagée par toutes les lignes.
 */
export function rowKey(row) {
  return row.mergeKey || rowIdentity(row);
}

const categoryKey = (name) => (name || "").trim().toLowerCase();

/**
 * Ancre d'une ligne produit dans le tableau d'un site.
 *
 * Définie ICI, à côté de rowKey : le récap et les tableaux doivent la calculer
 * exactement pareil pour que le clic tombe sur la bonne ligne. Deux formules qui
 * se ressemblent dans deux fichiers finiraient par diverger.
 *
 * La catégorie fait partie de l'ancre : un même produit peut figurer dans deux
 * catégories, et rowKey seul ne les distinguerait pas.
 */
export const rowAnchorId = (side, categoryName, row) =>
  `cp-${side}-row-${categoryKey(categoryName)}-${rowKey(row)}`;

/** Sous-catégorie d'une ligne, normalisée pour comparaison. */
const underKey = (row) => (row.underCategory || "").trim().toLowerCase();

/**
 * Ce qui compte comme une différence : ce que le CLIENT voit — le statut
 * (en carte / rupture / non affiché), la présence du produit sur la carte, et
 * l'endroit où il est rangé. Volontairement PAS le nombre en stock : deux sites
 * n'ont jamais les mêmes quantités, signaler ça noierait les vraies différences.
 *
 * Le classement compte parce qu'un client ne cherche pas au même endroit : le
 * Gaspacho est sous « Soupes » à Lyon 8 et sous « Entrées » à Lyon 9.
 */
function rowsDiffer(a, b) {
  if (!a || !b) return true;
  if (a.status !== b.status) return true;
  return underKey(a) !== underKey(b);
}

/**
 * @param {Object|null} offerA offre du site A (payload carte-preview)
 * @param {Object|null} offerB offre du site B
 * @returns {Array} sections fusionnées : [{ key, name, categories: [{ key, name,
 *   visibleA, visibleB, reasonsA, reasonsB, rows: [{ key, name, photo, kind,
 *   underCategory, a, b, differs }] }] }]
 *   `a`/`b` valent null quand la ligne n'existe pas dans le catalogue de ce site.
 */
export function mergeOffers(offerA, offerB) {
  const index = new Map();

  const visit = (offer, side) => {
    (offer?.sections || []).forEach((section) => {
      const sKey = categoryKey(section.name);
      if (!index.has(sKey)) {
        index.set(sKey, { key: sKey, name: section.name, categories: new Map() });
      }
      const sectionEntry = index.get(sKey);

      section.categories.forEach((category) => {
        const cKey = categoryKey(category.name);
        if (!sectionEntry.categories.has(cKey)) {
          sectionEntry.categories.set(cKey, {
            key: cKey,
            name: category.name,
            visibleA: null,
            visibleB: null,
            reasonsA: [],
            reasonsB: [],
            rows: new Map(),
          });
        }
        const categoryEntry = sectionEntry.categories.get(cKey);
        categoryEntry[side === "a" ? "visibleA" : "visibleB"] = category.visible;
        categoryEntry[side === "a" ? "reasonsA" : "reasonsB"] = category.reasons || [];

        category.products.forEach((row) => {
          const rKey = rowKey(row);
          if (!categoryEntry.rows.has(rKey)) {
            categoryEntry.rows.set(rKey, {
              key: rKey,
              kind: row.kind,
              name: row.name,
              photo: row.photo,
              underCategory: row.underCategory,
              a: null,
              b: null,
            });
          }
          const rowEntry = categoryEntry.rows.get(rKey);
          rowEntry[side] = row;
          // Le nom peut différer légèrement entre deux sites (le libellé est
          // saisi par établissement) : on garde celui du site A comme référence,
          // et on n'écrase avec celui de B que s'il manquait.
          if (side === "a" || !rowEntry.name) rowEntry.name = row.name;
          if (!rowEntry.photo) rowEntry.photo = row.photo;
        });
      });
    });
  };

  visit(offerA, "a");
  visit(offerB, "b");

  return Array.from(index.values()).map((section) => ({
    key: section.key,
    name: section.name,
    categories: Array.from(section.categories.values()).map((category) => ({
      ...category,
      rows: Array.from(category.rows.values()).map((row) => ({
        ...row,
        differs: rowsDiffer(row.a, row.b),
      })),
    })),
  }));
}

/** Nombre de lignes qui diffèrent, tous blocs confondus. */
export function countDifferences(sections) {
  return sections.reduce(
    (total, section) =>
      total +
      section.categories.reduce(
        (sub, category) => sub + category.rows.filter((r) => r.differs).length,
        0
      ),
    0
  );
}

/**
 * Une ligne compte comme écart CLIENT si au moins un des deux sites la montre.
 *
 * Deux produits absents des deux cartes peuvent différer côté back-office (l'un
 * désactivé ici, l'autre pas au catalogue là) sans qu'aucun client ne voie quoi
 * que ce soit : ces écarts-là ne valent pas une puce en haut de l'écran. Le
 * détail reste consultable dans les tableaux, filtre « Tout ».
 */
const rowSeenByClient = (row) =>
  (row.a && row.a.status !== "hidden") || (row.b && row.b.status !== "hidden");

/**
 * Écarts mis à plat, prêts à être listés en haut de l'écran.
 *
 * Ne remonte QUE ce que le client perçoit (cf. rowSeenByClient) : le récap doit
 * répondre à « qu'est-ce qui ne sera pas pareil sur les deux sites aujourd'hui »,
 * pas inventorier les divergences de réglage invisibles à la vente.
 *
 * Les catégories passent AVANT leurs produits : une catégorie absente ou masquée
 * d'un côté explique d'un coup toutes les lignes qu'elle contient — la lire
 * après les vingt produits qu'elle entraîne n'aurait aucun intérêt.
 *
 * @returns {Array} [{ key, type, section, category, name, kind, a, b, onlySide }]
 *   type : 'categoryMissing' | 'categoryHidden' | 'rowMissing' | 'rowStatus'
 *          | 'rowMovedCategory' (rangé dans une autre catégorie sur l'autre site)
 *          | 'rowMovedUnderCategory' (autre sous-catégorie, même catégorie)
 *   onlySide : 'a' ou 'b' — le site où l'élément est présent / affiché.
 */
export function describeDifferences(sections) {
  const categoryDiffs = [];
  const rowDiffs = [];

  // Où chaque produit est rangé sur chaque site, TOUTES catégories confondues.
  // Sans cet index, un produit déplacé d'une catégorie à l'autre apparaissait
  // comme absent de chaque côté — deux puces trompeuses pour un simple
  // changement de rangement.
  const placeA = new Map();
  const placeB = new Map();
  sections.forEach((section) =>
    section.categories.forEach((category) =>
      category.rows.forEach((row) => {
        if (row.a) placeA.set(row.key, { category: category.name, row: row.a });
        if (row.b) placeB.set(row.key, { category: category.name, row: row.b });
      })
    )
  );

  // Un déplacement se voit depuis les deux catégories concernées : on ne le
  // raconte qu'une fois.
  const movedReported = new Set();

  sections.forEach((section) => {
    section.categories.forEach((category) => {
      const base = { section: section.name, category: category.name };

      if (category.visibleA === null || category.visibleB === null) {
        // Catégorie absente d'un site : ses produits manquent forcément tous.
        // On les compte au lieu de les répéter un par un — seize puces « absent
        // du catalogue » derrière une puce qui l'a déjà dit noient les écarts
        // qu'on cherche vraiment. Le détail reste dans le tableau du site.
        //
        // Le compte ne retient que les produits que le client perd VRAIMENT :
        // annoncer « avec ses 16 produits » quand 4 seulement étaient en carte
        // surestimerait l'écart. Et s'il n'en reste aucun, la catégorie ne
        // changeait rien pour le client — pas de puce du tout.
        const rowCount = category.rows.filter(rowSeenByClient).length;
        if (!rowCount) return;

        categoryDiffs.push({
          ...base,
          key: `cat-missing:${section.key}:${category.key}`,
          type: "categoryMissing",
          onlySide: category.visibleA === null ? "b" : "a",
          rowCount,
        });
        return;
      }

      if (category.visibleA !== category.visibleB) {
        categoryDiffs.push({
          ...base,
          key: `cat-hidden:${section.key}:${category.key}`,
          type: "categoryHidden",
          onlySide: category.visibleA ? "a" : "b",
        });
      }

      category.rows
        .filter((row) => row.differs && rowSeenByClient(row))
        .forEach((row) => {
          const onlyA = row.a && !row.b;
          const onlyB = row.b && !row.a;

          if (onlyA || onlyB) {
            // Le produit est-il rangé ailleurs sur l'autre site ?
            const elsewhere = onlyA ? placeB.get(row.key) : placeA.get(row.key);
            if (elsewhere) {
              if (movedReported.has(row.key)) return;
              movedReported.add(row.key);
              rowDiffs.push({
                ...base,
                key: `moved:${row.key}`,
                type: "rowMovedCategory",
                name: row.name,
                kind: row.kind,
                a: onlyA ? row.a : elsewhere.row,
                b: onlyB ? row.b : elsewhere.row,
                categoryA: onlyA ? category.name : elsewhere.category,
                categoryB: onlyB ? category.name : elsewhere.category,
              });
              return;
            }

            rowDiffs.push({
              ...base,
              key: `row:${category.key}:${row.key}`,
              type: "rowMissing",
              onlySide: onlyB ? "b" : "a",
              name: row.name,
              kind: row.kind,
              a: row.a,
              b: row.b,
            });
            return;
          }

          // Présent des deux côtés : statut d'abord (c'est le plus lourd de
          // conséquences), rangement ensuite.
          rowDiffs.push({
            ...base,
            key: `row:${category.key}:${row.key}`,
            type: row.a.status !== row.b.status ? "rowStatus" : "rowMovedUnderCategory",
            onlySide: "a",
            name: row.name,
            kind: row.kind,
            a: row.a,
            b: row.b,
          });
        });
    });
  });

  return [...categoryDiffs, ...rowDiffs];
}
